import { createContext, memo, type ReactNode, useContext, useMemo } from 'react';

import { type BreadcrumbsItemType } from '@air/component-breadcrumbs';
import { reportErrorToBugsnag } from '@air/utils-error';

import { isDevOrTestStage } from '~/swr-hooks/utils';

export type PublicBoardNavigateToBoardParams = {
  boardId: string;
  /**
   * Seeds the centralized board after the navigation lands, so the drilled board renders its title
   * without waiting for the fetch. Omitted where the caller has no board record to seed from — no
   * seed happens then.
   */
  boardTitle?: string;
  /**
   * The page the board is listed under, for drills that DON'T target the viewer's current page.
   * Absent means the current page: each implementation resolves it itself (the URL implementation
   * from the route, the editor from its active page). `pageId`/`pageSlug` are `null` for Home/the
   * root page.
   */
  page?: { pageId: string | null; pageSlug: string | null };
};

export type PublicBoardContextValue = {
  /** Navigate the viewer into a board. Resolves once the navigation has been applied. */
  onNavigateToBoard: (params: PublicBoardNavigateToBoardParams) => Promise<void>;
  /**
   * The crumb that precedes the board trail in the asset modal's breadcrumbs — the hosting
   * surface's "where this board lives". The collection drill supplies its collection-home crumb
   * (built on the collection tree's own navigation, so the preview stays on its preview routes and
   * the editor canvas clears state instead of routing). ABSENT on the standalone board link — the
   * trail simply starts at the board's ancestors; that is its ONE meaning.
   */
  assetModalBreadcrumbPrefix?: BreadcrumbsItemType;
};

const defaultContextValue: PublicBoardContextValue = {
  onNavigateToBoard: () => Promise.resolve(),
};

const PublicBoardContext = createContext<PublicBoardContextValue>(defaultContextValue);

/*
 * PublicBoard's composition contract — its members are the board tree's own REQUIRED props, and
 * v2 PublicBoard mounts this provider internally (the AssetModal convention): mounting the tree
 * without a surface's bindings is a type error, so the strip test lives in the compiler. Named 1:1
 * with the component that is its boundary. Only members whose readers sit too deep for props live
 * here (table rows, kanban cards, the custom-fields panel's ancestors, the asset-modal header);
 * the topbar slots and gallery card renderers stay plain props because props reach them.
 *
 * | Surface                                   | Supplies                                          |
 * |-------------------------------------------|---------------------------------------------------|
 * | Standalone /a board page (PublicBoardPage) | URL drill (usePublicUrlBoardNavigation)           |
 * | Collection drill (PublicShowcaseBoardView) | the collection tree's own onNavigateToBoard —   |
 * |                                            | contracts cascade downward                        |
 * | Legacy below-medium board tree             | mounts this provider DIRECTLY at its boundary (it |
 * |                                            | cannot mount v2 PublicBoard); that mount dies     |
 * |                                            | with the legacy tree                              |
 */
export const usePublicBoard = () => {
  const ctx = useContext(PublicBoardContext);

  if (ctx === defaultContextValue) {
    const error = new Error('usePublicBoard must be used within PublicBoardProvider');

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({
      error,
      context: 'Public board context not found',
    });
  }

  return ctx;
};

export type PublicBoardProviderProps = PublicBoardContextValue & {
  children: ReactNode;
};

/**
 * Carries a surface's board-tree bindings into the public board tree beneath it. Mounted
 * internally by v2 PublicBoard itself (the bindings arrive as the board's required props); the
 * legacy below-medium board tree mounts it directly until that tree is deleted. The bindings are
 * passed in as props (rather than built here) so each surface owns its own behavior — this file
 * never imports any surface's code.
 */
export const PublicBoardProvider = memo(
  ({ assetModalBreadcrumbPrefix, children, onNavigateToBoard }: PublicBoardProviderProps) => {
    const contextValue = useMemo<PublicBoardContextValue>(
      () => ({ assetModalBreadcrumbPrefix, onNavigateToBoard }),
      [assetModalBreadcrumbPrefix, onNavigateToBoard],
    );

    return <PublicBoardContext.Provider value={contextValue}>{children}</PublicBoardContext.Provider>;
  },
);

PublicBoardProvider.displayName = 'PublicBoardProvider';
